import { useState } from "react";
import { Button } from "../components/ui/button";
import { X, Mail, User, Loader2, CheckCircle } from "lucide-react";

const AFFILIATE_URL = "https://bc072hg2p7rncg20qs5ev03po2.hop.clickbank.net";
const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxw6QnSbAtIgCHrtNFecCazUQNm5aA8h84hW4bZJgJ5U1Trn06JTN-VzUQLp1bGORix/exec";

export const EmailCaptureModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Method 1: Using URL parameters (most reliable for Google Apps Script)
      const params = new URLSearchParams();
      params.append("email", email);
      params.append("name", name || "Not provided");
      params.append("timestamp", new Date().toISOString());
      params.append("source", window.location.href);

      // Send using fetch with no-cors
      fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      }).catch(() => {
        // Silently handle - no-cors won't return readable response
      });

      // Method 2: Backup using image pixel (works even if fetch fails)
      const img = new Image();
      img.src = `${GOOGLE_SCRIPT_URL}?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name || "Not provided")}&timestamp=${encodeURIComponent(new Date().toISOString())}&source=${encodeURIComponent(window.location.href)}`;

      // Show success immediately
      setSuccess(true);
      // Wait briefly to show success, then redirect
      setTimeout(() => {
        window.open(AFFILIATE_URL, "_blank", "noopener,noreferrer");
        onClose();
        setSuccess(false);
        setEmail("");
        setName("");
      }, 1500);
    } catch (err) {
      // On error, still redirect to not lose the lead
      console.log("Email capture error, redirecting anyway:", err);
      window.open(AFFILIATE_URL, "_blank", "noopener,noreferrer");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    window.open(AFFILIATE_URL, "_blank", "noopener,noreferrer");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleSkip}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-slide-up">
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-[#2E7D32]" />
            </div>
            <h3 className="text-2xl font-bold text-[#1A1C19] mb-2" style={{ fontFamily: "Fraunces, serif" }}>
              You're In!
            </h3>
            <p className="text-[#4A5043]">Redirecting you now...</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-block bg-[#C41E3A] text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">
                🎁 EXCLUSIVE ACCESS
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#1A1C19] mb-2" style={{ fontFamily: "Fraunces, serif" }}>
                Wait! Get <span className="text-[#FF6B00]">FREE Bonuses</span>
              </h3>
              <p className="text-[#4A5043]">
                Enter your email to unlock exclusive discounts and bonuses worth $297!
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Your Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border-2 border-[#FFE4CC] rounded-xl focus:border-[#FF6B00] focus:outline-none transition-colors"
                />
              </div>

              <div className="relative">
                <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  placeholder="Your Email Address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                    error ? "border-[#C41E3A]" : "border-[#FFE4CC] focus:border-[#FF6B00]"
                  }`}
                  required
                />
              </div>

              {error && (
                <p className="text-[#C41E3A] text-sm">{error}</p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#FF6B00] to-[#FF8C00] hover:from-[#E65C00] hover:to-[#FF6B00] text-white font-bold rounded-xl py-4 shadow-lg hover:shadow-xl transition-all duration-300 text-lg disabled:opacity-70"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={20} className="animate-spin" />
                    Processing...
                  </span>
                ) : (
                  "👉 Unlock My Bonuses Now"
                )}
              </Button>
            </form>

            {/* Skip link */}
            <button
              onClick={handleSkip}
              className="w-full mt-4 text-center text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              No thanks, continue without bonuses →
            </button>

            {/* Trust text */}
            <p className="text-center text-xs text-gray-400 mt-4">
              🔒 Your information is 100% secure and will never be shared.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailCaptureModal;
